import type { Job } from "../types/Job";
import { errorTypeOf, softwareErrorsFrom } from "./organizeJobList";
import { GEO_VALIDATOR_SOURCE } from "./validateJobGeometry";

export type JobAlertSeverity = "error" | "warning" | "info";

/** One thing a scheduler should know about a job before putting it on a shift. */
export interface JobAlert {
	severity: JobAlertSeverity;
	/** A short lead-in shown before the text ("Field instructions"), when the text alone is unclear. */
	label?: string;
	text: string;
}

/**
 * What needs a scheduler's attention on a job, worst first: its Geo-Spatial Validator errors (one
 * per error type, worded as the first note of that type), how far past its due date it is, and its
 * field instructions. Empty for a job with none of these.
 */
export function jobAlerts(job: Job): JobAlert[] {
	const alerts: JobAlert[] = [];
	const seenTypes = new Set<string>();
	for (const error of softwareErrorsFrom(job, GEO_VALIDATOR_SOURCE)) {
		const type = errorTypeOf(error.note);
		if (seenTypes.has(type)) continue;
		seenTypes.add(type);
		alerts.push({ severity: "error", text: error.note });
	}
	// "Late (days)" is days past the Due Date: negative while the job is still ahead of it.
	if (job.daysLate && job.daysLate > 0) {
		alerts.push({ severity: "warning", text: `${job.daysLate} day${job.daysLate === 1 ? "" : "s"} past due` });
	}
	const instructions = job.fieldInstructions?.trim();
	if (instructions) alerts.push({ severity: "info", label: "Field instructions", text: instructions });
	return alerts;
}
